// PlatformHealth — 真實資料版（Route 15, PR #106）。

import React, { useCallback, useEffect, useState } from "react";
import { Activity, RefreshCw, ServerCrash, Database, Workflow } from "lucide-react";
import MetricCard from "../../components/MetricCard.js";
import EmptyState from "../../components/EmptyState.js";
import Skeleton from "../../components/Skeleton.js";
import { getPlatformHealth, friendlyPlatformError, type PlatformHealth } from "../../lib/platformApi.js";

type ListState =
  | { status: "loading" }
  | { status: "ready"; data: PlatformHealth }
  | { status: "error"; message: string };

const STATUS_COLOR: Record<string, string> = {
  ok: "text-success",
  degraded: "text-warning",
  error: "text-danger",
  timeout: "text-warning",
  unbound: "text-text-muted",
};
const STATUS_LABEL: Record<string, string> = {
  ok: "正常",
  degraded: "降級",
  error: "故障",
  timeout: "逾時",
  unbound: "未綁定",
};
const STATUS_DOT: Record<string, string> = {
  ok: "bg-success",
  degraded: "bg-warning",
  error: "bg-danger",
  timeout: "bg-warning",
  unbound: "bg-text-muted",
};

export default function PlatformHealth(): React.ReactElement {
  const [state, setState] = useState<ListState>({ status: "loading" });
  const load = useCallback(async () => {
    setState({ status: "loading" });
    try { const data = await getPlatformHealth(); setState({ status: "ready", data }); }
    catch (e) { setState({ status: "error", message: friendlyPlatformError(e) }); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-bg-surface p-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-2 text-text-primary"><Activity className="h-4 w-4 text-accent" /> 系統狀態</h2>
          <p className="text-2xs text-text-muted mt-1">客戶單位數、近 1 小時 RPS、LINE 用量、各 sub-worker 狀態與 D1 shard 概況。</p>
        </div>
        <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
          <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
        </button>
      </div>
      {state.status === "loading" && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">{[0,1,2].map(i => <div key={i} className="card p-4"><Skeleton className="h-3 w-24" /><Skeleton className="mt-3 h-8 w-32" /></div>)}</div>
      )}
      {state.status === "error" && <EmptyState icon={ServerCrash} title="健康資料載入失敗" description={state.message}
        action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />}
      {state.status === "ready" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <MetricCard label="啟用客戶單位" value={state.data.tenants_active} suffix=" 個" />
            <MetricCard label="近 1 小時 RPS" value={state.data.rps_1h} suffix=" /s" />
            <MetricCard label="LINE 今日用量" value={state.data.line_quota.sent_today} suffix={` / ${state.data.line_quota.monthly_cap}`} />
          </div>

          <div className="card p-4 sm:p-5">
            <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2"><Workflow className="h-4 w-4" /> 子服務狀態</h3>
            <ul className="divide-y divide-border">
              {state.data.probes.map((p) => (
                <li key={p.service} className="py-2 flex items-center gap-3">
                  <span className={`h-2 w-2 rounded-full ${STATUS_DOT[p.status] ?? "bg-text-muted"}`} />
                  <span className="font-mono text-xs text-text-primary w-40">{p.service}</span>
                  <span className={`text-2xs ${STATUS_COLOR[p.status] ?? "text-text-muted"}`}>{STATUS_LABEL[p.status] ?? p.status}</span>
                  <span className="text-2xs text-text-muted font-mono w-16 text-right">
                    {p.latency_ms == null ? "—" : `${p.latency_ms}ms`}
                  </span>
                  {p.build_sha && (
                    <span className="text-2xs text-text-muted font-mono w-20 truncate" title={p.build_sha}>
                      {p.build_sha.slice(0, 7)}
                    </span>
                  )}
                  <span className="text-2xs text-text-muted ml-auto truncate">{p.detail ?? ""}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card p-4 sm:p-5">
            <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2"><Database className="h-4 w-4" /> D1 shards</h3>
            <ul className="divide-y divide-border">
              {state.data.shards.map((s) => (
                <li key={s.name} className="py-2 flex items-center gap-3">
                  <span className="font-mono text-xs text-text-primary w-72">{s.name}</span>
                  <span className="text-2xs text-text-secondary">{s.kind}</span>
                  <span className="text-2xs text-text-muted ml-auto">{s.rows_approx == null ? "—" : `${s.rows_approx} 列（近似）`}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card p-4 sm:p-5">
            <h3 className="text-sm font-semibold text-text-primary mb-3">Queues</h3>
            <ul className="divide-y divide-border">
              {state.data.queues.map((q) => (
                <li key={q.name} className="py-2 flex items-center gap-3">
                  <span className="font-mono text-xs text-text-primary w-60">{q.name}</span>
                  <span className="text-2xs text-text-secondary">深度：{q.depth ?? "未取得"}</span>
                  {q.note && <span className="text-2xs text-text-muted ml-auto">{q.note}</span>}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
